import { POSITION_SEPARATOR } from '@/constants/separators'

export function itemSortByName<T>(items: T[] | undefined, extractor: (item: T) => string): T[] {
  if (!items) {
    return []
  }
  return JSON.parse(JSON.stringify(items)).sort((a: T, b: T) =>
    extractor(a).localeCompare(extractor(b)),
  )
}

export function itemSortByNumber<T>(items: T[] | undefined, extractor: (item: T) => number): T[] {
  if (!items) {
    return []
  }
  return JSON.parse(JSON.stringify(items)).sort((a: T, b: T) => extractor(a) - extractor(b))
}

export function comparePosition(a: string, b: string): number {
  const aParts = a.split(POSITION_SEPARATOR).map(Number)
  const bParts = b.split(POSITION_SEPARATOR).map(Number)
  const maxLength = Math.max(aParts.length, bParts.length)

  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] ?? 0
    const bPart = bParts[i] ?? 0
    if (aPart < bPart) return -1
    if (aPart > bPart) return 1
  }
  return 0
}

export function sortTree<T>(
  items: T[] | undefined,
  hasChildrenExtractor: (item: T) => boolean,
  nameExtractor: (item: T) => string,
): T[] {
  if (!items) {
    return []
  }

  const separator = POSITION_SEPARATOR
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const positionRegex = new RegExp(`^\\d+(?:${escapedSeparator}\\d+)*$`)

  const hasPositions = items.some((item) => {
    const name = nameExtractor(item)
    return positionRegex.test(name)
  })

  return [...items].sort((a: T, b: T) => {
    if (hasPositions) {
      const aName = nameExtractor(a)
      const bName = nameExtractor(b)

      const aIsPosition = positionRegex.test(aName)
      const bIsPosition = positionRegex.test(bName)

      if (aIsPosition && bIsPosition) {
        return comparePosition(aName, bName)
      }
      if (aIsPosition) return -1
      if (bIsPosition) return 1

      return aName.localeCompare(bName)
    }

    const aHasChildren = hasChildrenExtractor(a)
    const bHasChildren = hasChildrenExtractor(b)
    if (aHasChildren && !bHasChildren) {
      return -1
    }
    if (bHasChildren && !aHasChildren) {
      return 1
    }

    return nameExtractor(a).localeCompare(nameExtractor(b))
  })
}
